/**
 * Muir Way — Custom Topo Map Feature
 * asset: custom-topo-map.js
 *
 * Handles:
 *  - USGS TNM API search with debounce + pagination
 *  - Results rendering with thumbnails
 *  - Map selection state
 *  - Size / frame / personalization configurator
 *  - Shopify AJAX cart integration with line item properties
 *
 * No framework dependencies. ES6+. No build step required.
 * All DOM queries are scoped to this feature's container.
 */

'use strict';

/* ──────────────────────────────────────────────────────────────
   Constants
   ────────────────────────────────────────────────────────────── */
const TNM_API = 'https://usgs-proxy.jared-cb6.workers.dev/';
const DATASET = 'Historical Topographic Maps';
const PAGE_SIZE = 10;
const DEBOUNCE_MS = 450;

// Fixed prices keyed by size then frame option.
// 44x58 is unframed only — frame options are disabled in the UI for that size.
const PRICES = {
  '12x16': { 'none': 185, 'walnut': 395, 'black': 395 },
  '18x24': { 'none': 195, 'walnut': 440, 'black': 440 },
  '24x32': { 'none': 215, 'walnut': 540, 'black': 540 },
  '30x40': { 'none': 295, 'walnut': 670, 'black': 670 },
  '44x58': { 'none': 425 },
};

/* ──────────────────────────────────────────────────────────────
   State
   ────────────────────────────────────────────────────────────── */
let state = {
  query:         '',
  results:       [],
  total:         0,
  page:          0,      // 0-indexed page
  loading:       false,
  selectedMap:   null,   // full result object from TNM
  size:          '18x24',
  frame:         'none',
  customTitle:   '',
  customSubtitle: '',
  addonText:     false,
  addonIcon:     false,
  iconChoice:    '',
  iconLocation:  '',
  orderNote:     '',
  designNote:    '',
  error:         null,
};

/* ──────────────────────────────────────────────────────────────
   DOM References (populated in init())
   ────────────────────────────────────────────────────────────── */
let els = {};

/* ──────────────────────────────────────────────────────────────
   Utilities
   ────────────────────────────────────────────────────────────── */
function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function extractYear(dateStr) {
  if (!dateStr) return '—';
  return dateStr.split('-')[0];
}

function formatScale(raw) {
  // raw might be "24000" or "1:24,000" or "1:24000"
  if (!raw) return '';
  if (raw.includes(':')) return raw;
  const n = parseInt(raw, 10);
  if (!n) return raw;
  return '1:' + n.toLocaleString();
}

function currentPrice() {
  const sizeGroup = PRICES[state.size] || {};
  const base = state.size === '44x58'
    ? (sizeGroup['none'] || 425)
    : (sizeGroup[state.frame] || sizeGroup['none'] || 0);
  const addonPrices = window.CTM_ADDON_PRICES || { text: 15, icon: 10 };
  const extras = (state.addonText ? addonPrices.text : 0)
               + (state.addonIcon ? addonPrices.icon : 0);
  return base + extras;
}

function escapeHtml(str) {
  const d = document.createElement('div');
  d.textContent = str || '';
  return d.innerHTML;
}

/* ──────────────────────────────────────────────────────────────
   TNM API
   ────────────────────────────────────────────────────────────── */
async function fetchMaps(query, page = 0) {
  const offset = page * PAGE_SIZE;
  const params = new URLSearchParams({
    search: query,
    max: PAGE_SIZE,
    offset,
  });
  const url = `${TNM_API}?${params}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`TNM API error: ${res.status}`);
  return res.json();
}

/* ──────────────────────────────────────────────────────────────
   Search
   ────────────────────────────────────────────────────────────── */
async function doSearch(query, page = 0) {
  query = (query || '').trim();
  if (!query) return;

  state.query = query;
  state.page = page;
  state.loading = true;
  state.error = null;
  renderResults();

  try {
    const data = await fetchMaps(query, page);
    state.results = (data.items || []);
    state.total = data.total || 0;
    state.loading = false;
    renderResults();
  } catch (err) {
    state.loading = false;
    state.error = 'Could not reach the USGS map database. Please try again in a moment.';
    renderError();
  }
}

const debouncedSearch = debounce((q) => doSearch(q, 0), DEBOUNCE_MS);

/* ──────────────────────────────────────────────────────────────
   Rendering
   ────────────────────────────────────────────────────────────── */
function renderResults() {
  const panel = els.resultsPanel;

  if (state.loading) {
    panel.innerHTML = `
      <div class="ctm-results-loading" aria-live="polite" aria-busy="true">
        <div class="ctm-spinner"></div>
        Searching USGS archives…
      </div>`;
    return;
  }

  if (state.error) {
    renderError();
    return;
  }

  if (!state.query) {
    panel.innerHTML = `
      <div class="ctm-results-empty">
        <div class="ctm-results-empty__icon">🗺</div>
        <p>Enter a location above to browse<br>over 178,000 historical USGS topos.</p>
      </div>`;
    return;
  }

  if (state.results.length === 0) {
    panel.innerHTML = `
      <div class="ctm-results-empty">
        <div class="ctm-results-empty__icon">🔍</div>
        <p>No maps found for "<strong>${escapeHtml(state.query)}</strong>".<br>
        Try a nearby peak, quad name, or national park.</p>
      </div>`;
    return;
  }

  const totalPages = Math.ceil(state.total / PAGE_SIZE);
  const currentPage = state.page + 1;

  let html = `
    <div class="ctm-results-header">
      <span class="ctm-results-count" aria-live="polite">
        ${state.total.toLocaleString()} maps found
      </span>
    </div>
    <ul class="ctm-result-list" role="listbox" aria-label="Available maps">`;

  state.results.forEach((item, idx) => {
    const year = extractYear(item.publicationDate);
    const scale = formatScale(item.bestFitIndex || item.extent || '');
    const title = escapeHtml(item.title || 'Untitled');
    const thumb = item.previewGraphicURL || '';
    const isSelected = state.selectedMap && state.selectedMap.sourceId === item.sourceId;
    const hasPreview = !!thumb;
    const thumbHtml = thumb
      ? `<img src="${escapeHtml(thumb)}" alt="${title} map thumbnail" loading="lazy">`
      : `<div class="ctm-result-thumb--placeholder">◫</div>`;

    html += `
      <li class="ctm-result-item${isSelected ? ' is-selected' : ''}"
          role="option"
          aria-selected="${isSelected}"
          data-idx="${idx}"
          tabindex="0">
        <div class="ctm-result-thumb${hasPreview ? ' ctm-result-thumb--clickable' : ''}"
             data-preview-idx="${idx}" title="Preview on USGS TopoView" style="cursor:pointer;">
          ${thumbHtml}
          <div class="ctm-thumb-zoom-hint" aria-hidden="true">↗</div>
        </div>
        <div class="ctm-result-body">
          <div class="ctm-result-title">${title}</div>
          <div class="ctm-result-meta">USGS Historical Topographic Map</div>
          <div class="ctm-result-tags">
            ${year !== '—' ? `<span class="ctm-tag ctm-tag--year">${year}</span>` : ''}
            ${item.extent ? `<span class="ctm-tag ctm-tag--extent">${escapeHtml(item.extent)}</span>` : ''}
          </div>
        </div>
        <div class="ctm-result-action">
          <button class="ctm-preview-btn" data-preview-idx="${idx}" type="button" aria-label="Preview ${title} on USGS TopoView">Preview ↗</button>
          <button class="ctm-select-btn${isSelected ? ' is-selected' : ''}"
                  data-idx="${idx}"
                  type="button"
                  aria-pressed="${isSelected}">
            ${isSelected ? 'Selected ✓' : 'Select'}
          </button>
        </div>
      </li>`;
  });

  html += `</ul>`;

  if (totalPages > 1) {
    html += `
      <div class="ctm-pagination">
        <button class="ctm-page-btn" id="ctm-prev" ${state.page === 0 ? 'disabled' : ''}>
          ← Prev
        </button>
        <span class="ctm-page-info">Page ${currentPage} of ${totalPages}</span>
        <button class="ctm-page-btn" id="ctm-next" ${state.page >= totalPages - 1 ? 'disabled' : ''}>
          Next →
        </button>
      </div>`;
  }

  panel.innerHTML = html;

  // Bind preview button clicks (thumbnail and Preview button)
  panel.querySelectorAll('[data-preview-idx]').forEach(el => {
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = parseInt(el.dataset.previewIdx, 10);
      openPreview(state.results[idx]);
    });
  });

  // Bind select button clicks
  panel.querySelectorAll('.ctm-select-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = parseInt(btn.dataset.idx, 10);
      selectMap(state.results[idx]);
    });
  });

  // Keyboard: Enter on result row opens preview; Space selects
  panel.querySelectorAll('.ctm-result-item').forEach(el => {
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const idx = parseInt(el.dataset.idx, 10);
        const item = state.results[idx];
        if (item && item.previewGraphicURL) openPreview(item);
        else selectMap(item);
      }
      if (e.key === ' ') {
        e.preventDefault();
        const idx = parseInt(el.dataset.idx, 10);
        selectMap(state.results[idx]);
      }
    });
  });

  // Pagination
  const prevBtn = panel.querySelector('#ctm-prev');
  const nextBtn = panel.querySelector('#ctm-next');
  if (prevBtn) prevBtn.addEventListener('click', () => doSearch(state.query, state.page - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => doSearch(state.query, state.page + 1));
}

function renderError() {
  els.errorBanner.textContent = state.error || 'Something went wrong. Please try again.';
  els.errorBanner.classList.add('is-visible');
}

function renderSelectedBanner() {
  const map = state.selectedMap;
  if (!map) {
    els.selectedBanner.classList.remove('is-visible');
    return;
  }
  const year = extractYear(map.publicationDate);
  const thumb = map.previewGraphicURL || '';
  els.selectedBanner.classList.add('is-visible');
  els.selectedBanner.querySelector('.ctm-selected-banner__thumb').innerHTML = thumb
    ? `<img src="${escapeHtml(thumb)}" alt="">`
    : '';
  els.selectedBanner.querySelector('.ctm-selected-banner__name').textContent = map.title || '';
  els.selectedBanner.querySelector('.ctm-selected-banner__meta').textContent =
    [year !== '—' ? year : '', map.extent || ''].filter(Boolean).join(' · ');
}

function renderPrice() {
  els.priceDisplay.textContent = '$' + currentPrice();
}

function renderAddBtn() {
  els.addBtn.disabled = !state.selectedMap;
  els.addBtn.textContent = state.selectedMap
    ? 'Add to cart — $' + currentPrice()
    : 'Select a map to continue';
}

/* ──────────────────────────────────────────────────────────────
   Preview Lightbox (PDF via Cloudflare proxy)
   ────────────────────────────────────────────────────────────── */

// Cloudflare Worker proxy URL — strips Content-Disposition: attachment
// so the browser renders PDFs inline instead of downloading them.
const PROXY_URL = 'https://usgs-proxy.jared-cb6.workers.dev/';

function openPreview(item) {
  const overlay = document.getElementById('ctm-preview-overlay');
  if (!overlay) return;

  const year = extractYear(item.publicationDate);
  const title = item.title || 'Untitled';

  // Populate header
  overlay.querySelector('.ctm-lightbox__title').textContent = title;
  overlay.querySelector('.ctm-lightbox__meta').textContent = [
    year !== '—' ? year : '',
    item.extent || '',
  ].filter(Boolean).join(' · ');

  // Show overlay
  overlay.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  overlay.querySelector('.ctm-lightbox__close').focus();

  const viewerEl = document.getElementById('ctm-lightbox-viewer');
  if (!viewerEl) return;

  const pdfUrl = item.urls && item.urls.GeoPDF;

  if (pdfUrl) {
    // Route the PDF through our Cloudflare proxy which strips the
    // Content-Disposition: attachment header, allowing the browser
    // to render it inline inside the iframe.
    const proxiedUrl = PROXY_URL + '?url=' + encodeURIComponent(pdfUrl);
    viewerEl.innerHTML = `
      <div class="ctm-lightbox-loading" id="ctm-pdf-loading">
        <div class="ctm-spinner"></div>
        Loading map — may take 5–10 seconds…
      </div>
      <iframe
        src="${proxiedUrl}"
        class="ctm-lightbox-iframe"
        title="${escapeHtml(title)}"
        onload="var el=document.getElementById('ctm-pdf-loading'); if(el) el.style.display='none';"
      ></iframe>`;
  } else {
    viewerEl.innerHTML = '<div class="ctm-lightbox-loading">Preview not available for this map.</div>';
  }

  // Wire up Select button
  const selectBtn = overlay.querySelector('.ctm-lightbox__select-btn');
  selectBtn.onclick = () => { selectMap(item); closePreview(); };
}

function closePreview() {
  const overlay = document.getElementById('ctm-preview-overlay');
  if (!overlay) return;
  overlay.classList.remove('is-open');
  document.body.style.overflow = '';
  const viewerEl = document.getElementById('ctm-lightbox-viewer');
  if (viewerEl) viewerEl.innerHTML = '';
}

/* ──────────────────────────────────────────────────────────────
   Map Selection
   ────────────────────────────────────────────────────────────── */
function selectMap(item) {
  state.selectedMap = item;
  renderResults();
  renderSelectedBanner();
  renderAddBtn();
  // Scroll configurator into view on mobile
  if (window.innerWidth < 860) {
    els.configPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function clearSelection() {
  state.selectedMap = null;
  renderResults();
  renderSelectedBanner();
  renderAddBtn();
}

/* ──────────────────────────────────────────────────────────────
   Shopify Cart Integration
   ────────────────────────────────────────────────────────────── */
async function addToCart() {
  const map = state.selectedMap;
  if (!map) return;

  // Resolve the Shopify variant ID for the selected size.
  // The variant data is embedded in the section's Liquid template
  // as window.CTM_VARIANTS (see the .liquid file).
  const sizeVariants = window.CTM_VARIANTS && window.CTM_VARIANTS[state.size];
  const variantId = sizeVariants && sizeVariants[state.frame];
  if (!variantId) {
    alert('Could not find the product variant. Please contact us for help.');
    return;
  }

  const year = extractYear(map.publicationDate);

  // Prefer GeoTIFF source for production, fall back to JPEG
  const sourceUrl = (map.urls && (map.urls.GeoTIFF || map.urls['GeoTIFF '] || map.urls.JPEG))
    || map.downloadURL
    || '';

  const properties = {
    '_map_title':      map.title || '',
    '_map_year':       year,
    '_map_extent':     map.extent || '',
    '_map_source_url': sourceUrl,
    '_print_size':     state.size,
    '_frame':          state.frame,
  };
  if (state.customTitle)    properties['_custom_title']    = state.customTitle;
  if (state.customSubtitle) properties['_custom_subtitle'] = state.customSubtitle;
  if (state.orderNote)      properties['_order_note']      = state.orderNote;
  if (state.designNote)     properties['_design_note']     = state.designNote;

  els.addBtn.disabled = true;
  els.addBtn.textContent = 'Adding…';

  try {
    // Build array of items to add — main map product first, then any add-ons
    const items = [{ id: variantId, quantity: 1, properties }];

    const addonVariants = window.CTM_ADDON_VARIANTS || {};

    if (state.addonText && addonVariants.text) {
      items.push({
        id: addonVariants.text,
        quantity: 1,
        properties: {
          '_map_title':      map.title || '',
          '_custom_title':   state.customTitle,
          '_custom_subtitle': state.customSubtitle,
        },
      });
    }

    if (state.addonIcon && addonVariants.icon) {
      items.push({
        id: addonVariants.icon,
        quantity: 1,
        properties: {
          '_map_title':     map.title || '',
          '_icon_choice':   state.iconChoice || 'Not selected',
          '_icon_location': state.iconLocation || 'Not provided',
        },
      });
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error('Cart error');

    // Redirect to cart or open drawer — behaviour depends on theme
    if (window.CTM_OPEN_CART_DRAWER && typeof window.CTM_OPEN_CART_DRAWER === 'function') {
      window.CTM_OPEN_CART_DRAWER();
    } else {
      window.location.href = '/cart';
    }
  } catch (err) {
    els.addBtn.disabled = false;
    renderAddBtn();
    state.error = 'Could not add to cart. Please try again or contact us.';
    renderError();
  }
}

/* ──────────────────────────────────────────────────────────────
   Initialisation
   ────────────────────────────────────────────────────────────── */
function init() {
  const root = document.querySelector('[data-ctm-root]');
  if (!root) return;

  // Cache DOM references
  els = {
    searchInput:    root.querySelector('[data-ctm-search-input]'),
    searchBtn:      root.querySelector('[data-ctm-search-btn]'),
    resultsPanel:   root.querySelector('[data-ctm-results]'),
    selectedBanner: root.querySelector('[data-ctm-selected-banner]'),
    errorBanner:    root.querySelector('[data-ctm-error]'),
    priceDisplay:   root.querySelector('[data-ctm-price]'),
    addBtn:         root.querySelector('[data-ctm-add-btn]'),
    configPanel:    root.querySelector('[data-ctm-config]'),
    clearBtn:       root.querySelector('[data-ctm-clear]'),
  };

  // Search input
  els.searchInput.addEventListener('input', (e) => {
    debouncedSearch(e.target.value);
  });
  els.searchInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      doSearch(els.searchInput.value, 0);
    }
  });

  // Search button
  els.searchBtn.addEventListener('click', () => {
    doSearch(els.searchInput.value, 0);
  });

  // Quick-link buttons
  root.querySelectorAll('[data-ctm-quick]').forEach(btn => {
    btn.addEventListener('click', () => {
      const q = btn.dataset.ctmQuick;
      els.searchInput.value = q;
      root.querySelectorAll('[data-ctm-quick]').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      doSearch(q, 0);
    });
  });

  // Size options
  root.querySelectorAll('[data-ctm-size]').forEach(btn => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('[data-ctm-size]').forEach(b => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      state.size = btn.dataset.ctmSize;

      // 44x58 is unframed only — disable frame options and reset to none
      const frameSection = root.querySelector('[data-ctm-frame-section]');
      if (state.size === '44x58') {
        state.frame = 'none';
        const noneInput = root.querySelector('[data-ctm-frame][value="none"]');
        if (noneInput) noneInput.checked = true;
        if (frameSection) frameSection.style.opacity = '0.35';
        if (frameSection) frameSection.style.pointerEvents = 'none';
      } else {
        if (frameSection) frameSection.style.opacity = '';
        if (frameSection) frameSection.style.pointerEvents = '';
      }

      renderPrice();
      renderAddBtn();
    });
  });

  // Frame options
  root.querySelectorAll('[data-ctm-frame]').forEach(input => {
    input.addEventListener('change', () => {
      state.frame = input.value;
      renderPrice();
      renderAddBtn();
    });
  });

  // Personalization inputs
  const titleInput = root.querySelector('[data-ctm-custom-title]');
  const subInput   = root.querySelector('[data-ctm-custom-subtitle]');
  if (titleInput) titleInput.addEventListener('input', () => { state.customTitle = titleInput.value; });
  if (subInput)   subInput.addEventListener('input',   () => { state.customSubtitle = subInput.value; });

  // Add-on checkboxes — show/hide fields on toggle
  root.querySelectorAll('[data-ctm-addon]').forEach(cb => {
    const type = cb.dataset.ctmAddon;
    const fields = root.querySelector(`[data-ctm-addon-fields="${type}"]`);

    // Hide fields initially
    if (fields) fields.style.display = 'none';

    cb.addEventListener('change', () => {
      if (type === 'text') state.addonText = cb.checked;
      if (type === 'icon') state.addonIcon = cb.checked;
      if (fields) fields.style.display = cb.checked ? 'flex' : 'none';
      renderPrice();
      renderAddBtn();
    });
  });

  // Icon picker buttons
  root.querySelectorAll('[data-ctm-icon]').forEach(btn => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('[data-ctm-icon]').forEach(b => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');
      state.iconChoice = btn.dataset.ctmIcon;
    });
  });

  // Icon location input
  const iconLocationInput = root.querySelector('[data-ctm-icon-location]');
  if (iconLocationInput) {
    iconLocationInput.addEventListener('input', () => {
      state.iconLocation = iconLocationInput.value;
    });
  }

  // Order note
  const noteInput = root.querySelector('[data-ctm-order-note]');
  if (noteInput) {
    noteInput.addEventListener('input', () => {
      state.orderNote = noteInput.value;
    });
  }

  // Design note
  const designInput = root.querySelector('[data-ctm-design-note]');
  if (designInput) {
    designInput.addEventListener('input', () => {
      state.designNote = designInput.value;
    });
  }

  // Clear selection
  if (els.clearBtn) {
    els.clearBtn.addEventListener('click', clearSelection);
  }

  // Add to cart
  els.addBtn.addEventListener('click', addToCart);

  // ── Lightbox close handlers ──────────────────────────────────
  const overlay = document.getElementById('ctm-preview-overlay');
  if (overlay) {
    overlay.querySelector('.ctm-lightbox__close').addEventListener('click', closePreview);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closePreview(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closePreview();
    });
  }

  // Initial render
  renderResults();
  renderPrice();
  renderAddBtn();
}

// Run after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

/* ──────────────────────────────────────────────────────────────
   Mini Before/After Sliders
   ────────────────────────────────────────────────────────────── */
(function() {
  document.querySelectorAll('[data-ctm-mini-slider]').forEach(function(slider) {
    var after    = slider.querySelector('[data-mini-after]');
    var divider  = slider.querySelector('[data-mini-divider]');
    var dragging = false;

    function setPos(pct) {
      pct = Math.max(0, Math.min(100, pct));
      after.style.clipPath = 'inset(0 0 0 ' + pct + '%)';
      divider.style.left   = pct + '%';
    }

    function getPct(e) {
      var rect = slider.getBoundingClientRect();
      var x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      return (x / rect.width) * 100;
    }

    slider.addEventListener('mousedown',  function(e) { dragging = true; setPos(getPct(e)); e.preventDefault(); });
    slider.addEventListener('touchstart', function(e) { dragging = true; setPos(getPct(e)); }, { passive: true });
    window.addEventListener('mousemove',  function(e) { if (dragging) setPos(getPct(e)); });
    window.addEventListener('touchmove',  function(e) { if (dragging) setPos(getPct(e)); }, { passive: true });
    window.addEventListener('mouseup',    function()  { dragging = false; });
    window.addEventListener('touchend',   function()  { dragging = false; });
  });
})();

/* ──────────────────────────────────────────────────────────────
   Hero Thumbnails Lightbox
   ────────────────────────────────────────────────────────────── */
(function() {
  const overlay  = document.getElementById('ctm-hero-thumb-overlay');
  if (!overlay) return;
  const imgEl    = document.getElementById('ctm-hero-thumb-lightbox-img');
  const capEl    = document.getElementById('ctm-hero-thumb-lightbox-caption');
  const closeBtn = overlay.querySelector('.ctm-example-lightbox__close');
  const prevBtn  = overlay.querySelector('.ctm-example-lightbox__prev');
  const nextBtn  = overlay.querySelector('.ctm-example-lightbox__next');
  const items    = Array.from(document.querySelectorAll('.ctm-hero-thumb'));
  let current    = 0;

  function getItemData(item) {
    const img = item.querySelector('.ctm-hero-thumb__img');
    const wrap = item.closest('.ctm-hero-thumb-wrap');
    const cap = wrap ? wrap.querySelector('.ctm-hero-thumb__caption') : null;
    return { src: img ? img.src : '', alt: img ? img.alt : '', caption: cap ? cap.textContent : '' };
  }

  function openAt(idx) {
    current = (idx + items.length) % items.length;
    const data = getItemData(items[current]);
    imgEl.src = data.src;
    imgEl.alt = data.alt;
    capEl.textContent = data.caption;
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    imgEl.src = '';
  }

  items.forEach((item, idx) => item.addEventListener('click', () => openAt(idx)));
  closeBtn.addEventListener('click', close);
  prevBtn.addEventListener('click', () => openAt(current - 1));
  nextBtn.addEventListener('click', () => openAt(current + 1));
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  document.addEventListener('keydown', (e) => {
    if (!overlay.classList.contains('is-open')) return;
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft') openAt(current - 1);
    if (e.key === 'ArrowRight') openAt(current + 1);
  });
})();

/* ──────────────────────────────────────────────────────────────
   Before/After Slider Lightbox
   ────────────────────────────────────────────────────────────── */
(function() {
  const expandBtn  = document.getElementById('ctm-slider-expand-btn');
  const lightbox   = document.getElementById('ctm-slider-lightbox');
  const inner      = document.getElementById('ctm-slider-lightbox-inner');
  const closeBtn   = lightbox && lightbox.querySelector('.ctm-slider-lightbox__close');
  const sliderWrap = document.querySelector('.ctm-hero-slider');

  if (!expandBtn || !lightbox || !inner || !sliderWrap) return;

  function openSlider() {
    // Clone the entire slider block into the lightbox
    const sliderEl = sliderWrap.querySelector('[class^="ai-before-after-slider"]');
    if (!sliderEl) return;
    inner.innerHTML = '';
    const clone = sliderEl.cloneNode(true);
    inner.appendChild(clone);
    lightbox.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    closeBtn && closeBtn.focus();
  }

  function closeSlider() {
    lightbox.classList.remove('is-open');
    document.body.style.overflow = '';
    inner.innerHTML = '';
  }

  expandBtn.addEventListener('click', openSlider);
  closeBtn && closeBtn.addEventListener('click', closeSlider);
  lightbox.addEventListener('click', (e) => { if (e.target === lightbox) closeSlider(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && lightbox.classList.contains('is-open')) closeSlider();
  });
})();

/* ──────────────────────────────────────────────────────────────
   Example Photos Lightbox
   ────────────────────────────────────────────────────────────── */
(function() {
  const overlay   = document.getElementById('ctm-example-overlay');
  if (!overlay) return;

  const imgEl     = document.getElementById('ctm-example-lightbox-img');
  const capEl     = document.getElementById('ctm-example-lightbox-caption');
  const closeBtn  = overlay.querySelector('.ctm-example-lightbox__close');
  const prevBtn   = overlay.querySelector('.ctm-example-lightbox__prev');
  const nextBtn   = overlay.querySelector('.ctm-example-lightbox__next');
  const items     = Array.from(document.querySelectorAll('.ctm-example-item'));
  let current     = 0;

  function getItemData(item) {
    const img = item.querySelector('.ctm-example-item__img');
    // Caption is now outside the button, in the parent ctm-example-wrap
    const wrap = item.closest('.ctm-example-wrap') || item.parentElement;
    const cap = wrap ? wrap.querySelector('.ctm-example-item__caption') : null;
    return {
      src: img ? img.src : '',
      alt: img ? img.alt : '',
      caption: cap ? cap.textContent : ''
    };
  }

  function openAt(idx) {
    current = (idx + items.length) % items.length;
    const data = getItemData(items[current]);
    imgEl.src = data.src;
    imgEl.alt = data.alt;
    capEl.textContent = data.caption;
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    closeBtn.focus();
    prevBtn.style.display = items.length > 1 ? '' : 'none';
    nextBtn.style.display = items.length > 1 ? '' : 'none';
  }

  function close() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    imgEl.src = '';
  }

  items.forEach((item, idx) => {
    item.addEventListener('click', () => openAt(idx));
  });

  closeBtn.addEventListener('click', close);
  prevBtn.addEventListener('click', () => openAt(current - 1));
  nextBtn.addEventListener('click', () => openAt(current + 1));

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  document.addEventListener('keydown', (e) => {
    if (!overlay.classList.contains('is-open')) return;
    if (e.key === 'Escape')      close();
    if (e.key === 'ArrowLeft')   openAt(current - 1);
    if (e.key === 'ArrowRight')  openAt(current + 1);
  });
})();
